<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useDialogPluginComponent } from 'quasar';
import { useField, useForm } from 'vee-validate';
import * as yup from 'yup';
import { useI18n } from 'vue-i18n';

import type { Locales, ProjectDto } from '@/services/types/api.types';
import { useLanguageValidationErrors } from '@/composables';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import { languageEnabledArray, localizableString } from '@/helpers/validation.helper';

import LanguageSelector from '@/components/LanguageSelector.vue';
import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import LocalizedInput from '@/components/LocalizedInput.vue';
import { useCommunityStore, useProjectStore } from '@/stores';
import LocalizedEditor from '@/components/base/LocalizedEditor.vue';
import { getSupportedLocales } from '@/plugins/i18n';
import { Constants } from '@/constants';
import { provideEditLocale } from '@/composables/data-display-utils';

const { t } = useI18n();
const communityStore = useCommunityStore();
const projectStore = useProjectStore();

const { community } = storeToRefs(communityStore);

// PROPS
const props = defineProps<{
  project: ProjectDto;
  languageEnabled: Locales[];
}>();

const languageEnabled = ref<Locales[]>(props.project.language_enabled);

// DIALOG SET-UP
defineEmits([...useDialogPluginComponent.emits]);
const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();

// FORM
const { handleSubmit, errors, meta, setFieldValue, values } = useForm({
  initialValues: props.project,
  validationSchema: computed(() =>
    yup.object({
      title: localizableString(languageEnabled.value),
      description: localizableString(languageEnabled.value),
      clone_from_id: yup.number().optional(),
      language_enabled: languageEnabledArray(),
    }),
  ),
});

const languageErrors = useLanguageValidationErrors(errors);

const { value: languageEnabledFieldValue } = useField<Locales[]>('language_enabled');
const editLocale = provideEditLocale(languageEnabledFieldValue);
watch(languageEnabledFieldValue, () => {
  if (!languageEnabledFieldValue.value) return;
  getSupportedLocales()
    .filter((locale) => !languageEnabledFieldValue.value.includes(locale))
    .forEach((locale) => {
      setFieldValue(`title.${locale}`, undefined);
      setFieldValue(`description.${locale}`, undefined);
    });
  languageEnabledFieldValue.value.forEach((locale) => {
    if (!values.title?.[locale]) setFieldValue(`title.${locale}`, '');
    if (!values.description?.[locale]) setFieldValue(`description.${locale}`, '');
  });
  languageEnabled.value = languageEnabledFieldValue.value;
});

const onSubmit = handleSubmit(async (values) => {
  await wrapSavingOperation(
    async () => await projectStore.save(values),
    () => onDialogOK(),
  );
});
</script>
<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide" persistent>
    <q-card class="q-dialog-plugin" v-if="props.project" style="width: 600px">
      <q-card-section class="row">
        <div class="text-h6">{{ t('form.dialogs.project.create') }}</div>
        <q-space />
        <language-switcher
          v-model="editLocale"
          :has-validation-errors="languageErrors"
          :languageEnabled="languageEnabled"
        />
      </q-card-section>
      <q-separator />
      <q-card-section>
        <language-selector
          name="language_enabled"
          :label="t('project.languages')"
          :language-enabled="community?.language_enabled"
        />
        <localized-input
          name="title"
          label="Titre"
          :editLocale="editLocale"
          outlined
          :language_enabled="languageEnabled"
        />
        <q-field :label="t('global.label.content')" stack-label>
          <template v-slot:control>
            <localized-editor
              name="description"
              :label="t('global.label.content')"
              class="q-mt-sm full-width"
              :editLocale="editLocale"
              :language_enabled="languageEnabled"
            />
          </template>
        </q-field>
      </q-card-section>
      <q-separator />
      <q-card-actions align="right">
        <q-btn color="primary" flat :label="t('global.cancel')" @click="onDialogCancel" />
        <q-btn color="primary" :label="t('global.save')" :disabled="!meta.valid" @click="onSubmit" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
